import { useCallback, useRef, useState } from 'react';
import { Service } from '../../core/_models';
import { BASE_API_URL, createServiceQRCodePDF } from '../../core/_requests';
import { APIError, useAxios } from '../../core/_axios';

function ServiceQRCodeFrame({
    service,
    width = '500px'
}: {
    service: Service,
    width?: string
}) {
    const axios = useAxios();

    const svgRef = useRef<SVGSVGElement>(null);

    // Calculate the aspect ratio based on the original SVG dimensions
    const aspectRatio = 5376 / 4048; // this is the height / width

    // Set the height based on the width and aspect ratio
    const calculatedHeight = typeof width === 'string' && width.endsWith('%')
        ? `calc(${width} * ${aspectRatio})`
        : `${parseFloat(width) * aspectRatio}px`;

    const serviceQrCode = `${BASE_API_URL}/api/v1/app/anon/services/${service.unique_identifier}/qr-code`;

    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [downloadingPNG, setDownloadingPNG] = useState<boolean>(false);
    const [downloadStatus, setDownloadStatus] = useState<string | null>(null);

    const convertServiceToPDF = useCallback(async () => {
        setDownloadingPNG(true);
        setDownloadStatus("Creating PDF...");
        const svgElement = svgRef.current;

        if (!svgElement) {
            setErrorMessage('SVG element not found.');
            setDownloadingPNG(false);
            setDownloadStatus(null);
            return;
        }

        try {
            const resultingPDFURL = await createServiceQRCodePDF(axios, service.unique_identifier, {
                svg: svgElement.outerHTML
            });
            // serve this file:
            // Use the resulting URL to trigger the download
            const link = document.createElement('a');
            link.href = resultingPDFURL;
            link.download = `${service.unique_identifier}.pdf`; // Customize the filename as needed
            document.body.appendChild(link);
            link.click();

            // Cleanup: remove the link
            document.body.removeChild(link);
        } catch (exception) {
            console.error(exception);
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.';
            setErrorMessage(errorMessage);
        } finally {
            setDownloadingPNG(false);
            setDownloadStatus(null);
        }
    }, [axios, service.unique_identifier]);

    return (
        <>
            <div className='mb-3'>
                <label className="block text-sm font-medium text-gray-900 dark:text-white">
                    Print QR Code with Frame:
                </label>
            </div>
            {errorMessage && <div>
                <div className="alert alert-danger">{errorMessage}</div>
            </div>}
            {true && <div className='mb-3'>
                <button
                    onClick={convertServiceToPDF}
                    type="button" className="btn btn-light me-3"
                    disabled={downloadingPNG}
                >
                    {!downloadStatus ? `Download PDF` : downloadStatus}
                </button>
            </div>}
            <div className='max-w-full' style={{
                backgroundColor: '#fafafa',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
            }}>
                <svg
                    id="svg-element"
                    ref={svgRef}
                    width={width}
                    height={calculatedHeight}
                    viewBox="0 0 4048 5376"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    preserveAspectRatio="xMidYMid meet"
                >
                    <title>qr_code_with_frame</title>
                    <g>
                        <image
                            href="https://upload.serviceguestbook.com/design/blank-qr-code-template.png"
                            x="0"
                            y="0"
                            width="4048"
                            height="5376"
                            preserveAspectRatio="xMidYMid meet"
                        />
                    </g>
                    <g id="Frames" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                        <g id="qr_code_with_frame" transform="translate(0, -100)">
                            <text id="Funeral-Home-Name"
                                fontFamily="Roboto-Medium, Roboto"
                                fontSize="150"
                                fontWeight="400"
                                fill="#000000"
                                x="50%"
                                y="1546"
                                textAnchor="middle"
                                dominantBaseline="middle">
                                {service.account.name}
                            </text>
                            <text id="To-sign-the-Service"
                                fontFamily="Roboto-Medium, Roboto"
                                fontSize="150"
                                fontWeight="400"
                                fill="#000000"
                                x="50%"
                                y="2283"
                                textAnchor="middle"
                                dominantBaseline="middle">
                                To sign the Signature Guestbook for:
                            </text>
                            <text id="Christopher-Lee-Coho"
                                fontFamily="Roboto-Medium, Roboto"
                                fontSize="200"
                                fontWeight="400"
                                fill="#000000"
                                x="50%"
                                y="2656"
                                textAnchor="middle"
                                dominantBaseline="middle">
                                {service.public_data.deceased_person.full_name}
                            </text>
                            <rect id="Rectangle"
                                stroke="#C8A682"
                                strokeWidth="12"
                                fill="#FFFFFF"
                                x="1274"
                                y="2974"
                                width="1500"
                                height="1500">
                            </rect>
                            <image
                                href={serviceQrCode}
                                x="1274"
                                y="2974"
                                width="1500"
                                height="1500"
                                preserveAspectRatio="xMidYMid meet"
                            />
                            <text id="SCAN-HERE"
                                fontFamily="Roboto-Medium, Roboto"
                                fontSize="250"
                                fontWeight="400"
                                fill="#000000"
                                x="50%"
                                y="1957"
                                textAnchor="middle"
                                dominantBaseline="middle">
                                SCAN HERE
                            </text>
                        </g>
                    </g>
                </svg>
            </div>
        </>
    );
}

export default ServiceQRCodeFrame;
